import { isTimePermissionEnabled } from '../config/applicationFeatures';
import React from 'react';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { routes, type RootStackParamList } from '../constants/routes';
import { useAuth } from '../context/AuthContext';
import { ChangePasswordScreen } from '../screens/auth/ChangePasswordScreen';
import { UnauthorizedScreen } from '../screens/system/UnauthorizedScreen';
import { ApiStatusScreen } from '../screens/system/ApiStatusScreen';
import { ProfileScreen } from '../screens/profile/ProfileScreen';
import { ProfileDetailsScreen } from '../screens/profile/ProfileDetailsScreen';
import { SettingsScreen } from '../screens/settings/SettingsScreen';
import { LateArrivalConfigurationScreen } from '../screens/settings/LateArrivalConfigurationScreen';
import { AboutScreen } from '../screens/settings/AboutScreen';
import { SplashScreen } from '../screens/system/SplashScreen';
import { AttendanceDetailsScreen } from '../screens/attendance/AttendanceDetailsScreen';
import { LocationTrackingDetailsScreen } from '../screens/attendance/LocationTrackingDetailsScreen';
import { AttendanceFilterScreen } from '../screens/attendance/AttendanceFilterScreen';
import { AttendanceHistoryScreen } from '../screens/attendance/AttendanceHistoryScreen';
import { LeaveApplicationDetailsScreen } from '../screens/leave/LeaveApplicationDetailsScreen';
import { LeaveApplicationListScreen } from '../screens/leave/LeaveApplicationListScreen';
import { LeaveFilterScreen } from '../screens/leave/LeaveFilterScreen';
import { AdminLeaveRequestListScreen } from '../screens/leave/AdminLeaveRequestListScreen';
import { LeaveApprovalScreen } from '../screens/leave/LeaveApprovalScreen';
import { LeaveConfigurationListScreen } from '../screens/leave/LeaveConfigurationListScreen';
import { LeaveConfigurationCreateScreen } from '../screens/leave/LeaveConfigurationCreateScreen';
import { LeaveBalanceListScreen } from '../screens/leave/LeaveBalanceListScreen';
import { LeaveBalanceEmployeeScreen } from '../screens/leave/LeaveBalanceEmployeeScreen';
import { LeaveBalanceUpdateScreen } from '../screens/leave/LeaveBalanceUpdateScreen';
import { EmployeeDetailsScreen } from '../screens/employees/EmployeeDetailsScreen';
import { EmployeeTimeOffBalanceScreen } from '../screens/employees/EmployeeTimeOffBalanceScreen';
import { EmployeeCreateScreen } from '../screens/employees/EmployeeCreateScreen';
import { EmployeeEditScreen } from '../screens/employees/EmployeeEditScreen';
import { EmployeeAssignShiftScreen } from '../screens/employees/EmployeeAssignShiftScreen';
import { EmployeeFaceRegistrationScreen } from '../screens/employees/EmployeeFaceRegistrationScreen';
import { FaceEnrollmentCameraScreen } from '../screens/employees/FaceEnrollmentCameraScreen';
import { FaceAdminScreen } from '../screens/face/FaceAdminScreen';
import { FaceIdentifyScreen } from '../screens/face/FaceIdentifyScreen';
import { AdminWfhRequestListScreen } from '../screens/wfh/AdminWfhRequestListScreen';
import { WfhApprovalScreen } from '../screens/wfh/WfhApprovalScreen';
import { WfhFilterScreen } from '../screens/wfh/WfhFilterScreen';
import { WfhHomeScreen } from '../screens/wfh/WfhHomeScreen';
import { WfhRequestDetailsScreen } from '../screens/wfh/WfhRequestDetailsScreen';
import { WfhRequestListScreen } from '../screens/wfh/WfhRequestListScreen';
import { WfhConfigurationListScreen } from '../screens/wfh/WfhConfigurationListScreen';
import { WfhConfigurationCreateScreen } from '../screens/wfh/WfhConfigurationCreateScreen';
import { AdminTimePermissionRequestListScreen } from '../screens/timePermission/AdminTimePermissionRequestListScreen';
import { TimePermissionApprovalScreen } from '../screens/timePermission/TimePermissionApprovalScreen';
import { TimePermissionFilterScreen } from '../screens/timePermission/TimePermissionFilterScreen';
import { TimePermissionHomeScreen } from '../screens/timePermission/TimePermissionHomeScreen';
import { TimePermissionRequestDetailsScreen } from '../screens/timePermission/TimePermissionRequestDetailsScreen';
import { TimePermissionRequestListScreen } from '../screens/timePermission/TimePermissionRequestListScreen';
import { PermissionConfigurationListScreen } from '../screens/timePermission/PermissionConfigurationListScreen';
import { PermissionConfigurationCreateScreen } from '../screens/timePermission/PermissionConfigurationCreateScreen';
import { PermissionConfigurationPolicyScreen } from '../screens/timePermission/PermissionConfigurationPolicyScreen';
import { AttendanceCorrectionHomeScreen } from '../screens/attendance/AttendanceCorrectionHomeScreen';
import { AttendanceCorrectionRequestListScreen } from '../screens/attendance/AttendanceCorrectionRequestListScreen';
import { AttendanceCorrectionFilterScreen } from '../screens/attendance/AttendanceCorrectionFilterScreen';
import { AttendanceCorrectionRequestDetailsScreen } from '../screens/attendance/AttendanceCorrectionRequestDetailsScreen';
import { AdminAttendanceCorrectionRequestListScreen } from '../screens/attendance/AdminAttendanceCorrectionRequestListScreen';
import { AttendanceCorrectionApprovalScreen } from '../screens/attendance/AttendanceCorrectionApprovalScreen';
import { WorkflowDetailsScreen } from '../screens/workflow/WorkflowDetailsScreen';
import { WorkflowFilterScreen } from '../screens/workflow/WorkflowFilterScreen';
import { WorkflowInboxScreen } from '../screens/workflow/WorkflowInboxScreen';
import { NotificationDetailsScreen } from '../screens/notifications/NotificationDetailsScreen';
import { NotificationListScreen } from '../screens/notifications/NotificationListScreen';
import { NotificationPreferencesScreen } from '../screens/notifications/NotificationPreferencesScreen';
import { ReportDownloadResultScreen } from '../screens/reports/ReportDownloadResultScreen';
import { ReportFilterScreen } from '../screens/reports/ReportFilterScreen';
import { ReportListScreen } from '../screens/reports/ReportListScreen';
import { ReportPreviewScreen } from '../screens/reports/ReportPreviewScreen';
import { AttendanceReportScreen } from '../screens/reports/AttendanceReportScreen';
import { TeamAttendanceReportScreen } from '../screens/reports/TeamAttendanceReportScreen';
import { LeaveReportScreen } from '../screens/reports/LeaveReportScreen';
import { MonthlyAttendanceReportScreen } from '../screens/reports/MonthlyAttendanceReportScreen';
import { WeeklyAttendanceEmployeeScreen } from '../screens/reports/WeeklyAttendanceEmployeeScreen';
import { EmployeeOverallWeeklyReportScreen } from '../screens/reports/EmployeeOverallWeeklyReportScreen';
import { EmployeeMonthlyAttendanceReportScreen } from '../screens/reports/EmployeeMonthlyAttendanceReportScreen';
import { WeeklyAttendanceReportScreen } from '../screens/reports/WeeklyAttendanceReportScreen';
import { MyWeeklyAttendanceScreen } from '../screens/attendance/MyWeeklyAttendanceScreen';
import { WfhReportScreen } from '../screens/reports/WfhReportScreen';
import { AdminDashboardScreen } from '../screens/dashboard/AdminDashboardScreen';
import { DashboardScreen } from '../screens/dashboard/DashboardScreen';
import { TeamDashboardScreen } from '../screens/dashboard/TeamDashboardScreen';
import { OrganizationHomeScreen } from '../screens/organization/OrganizationHomeScreen';
import { OrganizationDetailsScreen } from '../screens/organization/OrganizationDetailsScreen';
import { EditOrganizationScreen } from '../screens/organization/EditOrganizationScreen';
import { OrganizationListScreen } from '../screens/organization/OrganizationListScreen';
import { OrganizationCreateScreen } from '../screens/organization/OrganizationCreateScreen';
import { OrganizationCreatedScreen } from '../screens/organization/OrganizationCreatedScreen';
import { ManagedOrganizationDetailsScreen } from '../screens/organization/ManagedOrganizationDetailsScreen';
import { OrganizationHrAdminCreateScreen } from '../screens/organization/OrganizationHrAdminCreateScreen';
import { LocationListScreen } from '../screens/organization/LocationListScreen';
import { LocationDetailsScreen } from '../screens/organization/LocationDetailsScreen';
import { LocationFormScreen } from '../screens/organization/LocationFormScreen';
import { DepartmentListScreen } from '../screens/organization/DepartmentListScreen';
import { DepartmentDetailsScreen } from '../screens/organization/DepartmentDetailsScreen';
import { DepartmentFormScreen } from '../screens/organization/DepartmentFormScreen';
import { DesignationListScreen } from '../screens/organization/DesignationListScreen';
import { DesignationDetailsScreen } from '../screens/organization/DesignationDetailsScreen';
import { DesignationFormScreen } from '../screens/organization/DesignationFormScreen';
import { TeamListScreen } from '../screens/organization/TeamListScreen';
import { TeamDetailsScreen } from '../screens/organization/TeamDetailsScreen';
import { TeamFormScreen } from '../screens/organization/TeamFormScreen';
import { ShiftListScreen } from '../screens/shift/ShiftListScreen';
import { ShiftDetailsScreen } from '../screens/shift/ShiftDetailsScreen';
import { ShiftFormScreen } from '../screens/shift/ShiftFormScreen';
import { WorkPolicyListScreen } from '../screens/workPolicy/WorkPolicyListScreen';
import { WorkPolicyDetailsScreen } from '../screens/workPolicy/WorkPolicyDetailsScreen';
import { WorkPolicyFormScreen } from '../screens/workPolicy/WorkPolicyFormScreen';
import { KioskListScreen } from '../screens/kiosk/KioskListScreen';
import { KioskCreateScreen } from '../screens/kiosk/KioskCreateScreen';
import { KioskDetailsScreen } from '../screens/kiosk/KioskDetailsScreen';
import { KioskActivationQrScreen } from '../screens/kiosk/KioskActivationQrScreen';
import { ProjectListScreen } from '../screens/projects/ProjectListScreen';
import { ProjectFormScreen } from '../screens/projects/ProjectFormScreen';
import { ProjectEditScreen } from '../screens/projects/ProjectEditScreen';
import { ProjectTaskBoardScreen } from '../screens/projects/ProjectTaskBoardScreen';
import { ProjectReportScreen } from '../screens/projects/ProjectReportScreen';
import { ProjectTaskDetailsScreen } from '../screens/projects/ProjectTaskDetailsScreen';
import { VisitorListScreen } from '../screens/visitors/VisitorListScreen';
import { VisitorRegisterScreen } from '../screens/visitors/VisitorRegisterScreen';
import { VisitorFaceScanScreen } from '../screens/visitors/VisitorFaceScanScreen';
import { VisitorHistoryScreen } from '../screens/visitors/VisitorHistoryScreen';
import { OfficeCalendarScreen } from '../screens/calendar/OfficeCalendarScreen';
import { HolidayCalendarListScreen } from '../screens/calendar/HolidayCalendarListScreen';
import { HolidayCalendarFormScreen } from '../screens/calendar/HolidayCalendarFormScreen';
import { MainTabNavigator } from './MainTabNavigator';
import { AuthNavigator } from './AuthNavigator';

const Stack = createNativeStackNavigator<RootStackParamList>();

export function RootNavigator() {
  const { status } = useAuth();

  if (status === 'initializing') return <SplashScreen />;

  return (
    <Stack.Navigator screenOptions={{ headerShown: false }}>
      {status === 'authenticated' ? (
        <>
          <Stack.Screen name={routes.mainTabs} component={MainTabNavigator} />
          <Stack.Screen name={routes.dashboard} component={DashboardScreen} />
          <Stack.Screen
            name={routes.adminDashboard}
            component={AdminDashboardScreen}
          />
          <Stack.Screen
            name={routes.teamDashboard}
            component={TeamDashboardScreen}
          />
          <Stack.Screen
            name={routes.changePassword}
            component={ChangePasswordScreen}
          />
          <Stack.Screen
            name={routes.unauthorized}
            component={UnauthorizedScreen}
          />
          <Stack.Screen name={routes.profile} component={ProfileScreen} />
          <Stack.Screen
            name={routes.profileDetails}
            component={ProfileDetailsScreen}
          />
          <Stack.Screen name={routes.settings} component={SettingsScreen} />
          <Stack.Screen
            name={routes.lateArrivalConfiguration}
            component={LateArrivalConfigurationScreen}
          />
          <Stack.Screen name={routes.about} component={AboutScreen} />
          <Stack.Screen name={routes.apiStatus} component={ApiStatusScreen} />
          <Stack.Screen
            name={routes.attendanceHistory}
            component={AttendanceHistoryScreen}
          />
          <Stack.Screen
            name={routes.attendanceFilter}
            component={AttendanceFilterScreen}
          />
          <Stack.Screen
            name={routes.attendanceDetails}
            component={AttendanceDetailsScreen}
          />
          <Stack.Screen
            name={routes.locationTrackingDetails}
            component={LocationTrackingDetailsScreen}
          />
          <Stack.Screen
            name={routes.leaveApplications}
            component={LeaveApplicationListScreen}
          />
          <Stack.Screen
            name={routes.leaveFilter}
            component={LeaveFilterScreen}
          />
          <Stack.Screen
            name={routes.leaveDetails}
            component={LeaveApplicationDetailsScreen}
          />
          <Stack.Screen
            name={routes.adminLeaveRequests}
            component={AdminLeaveRequestListScreen}
          />
          <Stack.Screen
            name={routes.leaveApproval}
            component={LeaveApprovalScreen}
          />
          <Stack.Screen name={routes.wfhHome} component={WfhHomeScreen} />
          <Stack.Screen
            name={routes.wfhRequests}
            component={WfhRequestListScreen}
          />
          <Stack.Screen name={routes.wfhFilter} component={WfhFilterScreen} />
          <Stack.Screen
            name={routes.wfhDetails}
            component={WfhRequestDetailsScreen}
          />
          <Stack.Screen
            name={routes.adminWfhRequests}
            component={AdminWfhRequestListScreen}
          />
          <Stack.Screen
            name={routes.wfhApproval}
            component={WfhApprovalScreen}
          />
          {isTimePermissionEnabled() ? <Stack.Screen
            name={routes.timePermissionHome}
            component={TimePermissionHomeScreen}
          /> : null}
          {isTimePermissionEnabled() ? <Stack.Screen
            name={routes.timePermissionRequests}
            component={TimePermissionRequestListScreen}
          /> : null}
          {isTimePermissionEnabled() ? <Stack.Screen
            name={routes.timePermissionFilter}
            component={TimePermissionFilterScreen}
          /> : null}
          {isTimePermissionEnabled() ? <Stack.Screen
            name={routes.timePermissionDetails}
            component={TimePermissionRequestDetailsScreen}
          /> : null}
          {isTimePermissionEnabled() ? <Stack.Screen
            name={routes.adminTimePermissionRequests}
            component={AdminTimePermissionRequestListScreen}
          /> : null}
          {isTimePermissionEnabled() ? <Stack.Screen
            name={routes.timePermissionApproval}
            component={TimePermissionApprovalScreen}
          /> : null}
          <Stack.Screen
            name={routes.attendanceCorrectionHome}
            component={AttendanceCorrectionHomeScreen}
          />
          <Stack.Screen
            name={routes.attendanceCorrectionRequests}
            component={AttendanceCorrectionRequestListScreen}
          />
          <Stack.Screen
            name={routes.attendanceCorrectionFilter}
            component={AttendanceCorrectionFilterScreen}
          />
          <Stack.Screen
            name={routes.attendanceCorrectionDetails}
            component={AttendanceCorrectionRequestDetailsScreen}
          />
          <Stack.Screen
            name={routes.adminAttendanceCorrectionRequests}
            component={AdminAttendanceCorrectionRequestListScreen}
          />
          <Stack.Screen
            name={routes.attendanceCorrectionApproval}
            component={AttendanceCorrectionApprovalScreen}
          />
          <Stack.Screen
            name={routes.workflowInbox}
            component={WorkflowInboxScreen}
          />
          <Stack.Screen
            name={routes.workflowFilter}
            component={WorkflowFilterScreen}
          />
          <Stack.Screen
            name={routes.workflowDetails}
            component={WorkflowDetailsScreen}
          />
          <Stack.Screen
            name={routes.notificationList}
            component={NotificationListScreen}
          />
          <Stack.Screen
            name={routes.notificationDetails}
            component={NotificationDetailsScreen}
          />
          <Stack.Screen
            name={routes.notificationPreferences}
            component={NotificationPreferencesScreen}
          />
          <Stack.Screen name={routes.reportList} component={ReportListScreen} />
          <Stack.Screen
            name={routes.reportFilter}
            component={ReportFilterScreen}
          />
          <Stack.Screen
            name={routes.reportPreview}
            component={ReportPreviewScreen}
          />
          <Stack.Screen
            name={routes.reportDownloadResult}
            component={ReportDownloadResultScreen}
          />
          <Stack.Screen
            name={routes.attendanceReport}
            component={AttendanceReportScreen}
          />
          <Stack.Screen
            name={routes.teamAttendanceReport}
            component={TeamAttendanceReportScreen}
          />
          <Stack.Screen
            name={routes.monthlyAttendanceReport}
            component={MonthlyAttendanceReportScreen}
          />
          <Stack.Screen
            name={routes.weeklyAttendanceReport}
            component={WeeklyAttendanceReportScreen}
          />
          <Stack.Screen
            name={routes.employeeOverallWeeklyReport}
            component={EmployeeOverallWeeklyReportScreen}
          />
          <Stack.Screen
            name={routes.employeeMonthlyAttendanceReport}
            component={EmployeeMonthlyAttendanceReportScreen}
          />
          <Stack.Screen
            name={routes.weeklyAttendanceEmployee}
            component={WeeklyAttendanceEmployeeScreen}
          />
          <Stack.Screen
            name={routes.myWeeklyAttendance}
            component={MyWeeklyAttendanceScreen}
          />
          <Stack.Screen name={routes.wfhReport} component={WfhReportScreen} />
          <Stack.Screen
            name={routes.leaveReport}
            component={LeaveReportScreen}
          />
          <Stack.Screen
            name={routes.organizationHome}
            component={OrganizationHomeScreen}
          />
          <Stack.Screen
            name={routes.organizationDetails}
            component={OrganizationDetailsScreen}
          />
          <Stack.Screen
            name={routes.organizationEdit}
            component={EditOrganizationScreen}
          />
          <Stack.Screen name={routes.organizationList} component={OrganizationListScreen} />
          <Stack.Screen name={routes.organizationCreate} component={OrganizationCreateScreen} />
          <Stack.Screen name={routes.organizationCreated} component={OrganizationCreatedScreen} />
          <Stack.Screen
            name={routes.managedOrganizationDetails}
            component={ManagedOrganizationDetailsScreen}
          />
          <Stack.Screen
            name={routes.organizationHrAdminCreate}
            component={OrganizationHrAdminCreateScreen}
          />
          <Stack.Screen
            name={routes.locationList}
            component={LocationListScreen}
          />
          <Stack.Screen
            name={routes.locationDetails}
            component={LocationDetailsScreen}
          />
          <Stack.Screen
            name={routes.locationCreate}
            component={LocationFormScreen}
          />
          <Stack.Screen
            name={routes.locationEdit}
            component={LocationFormScreen}
          />
          <Stack.Screen
            name={routes.departmentList}
            component={DepartmentListScreen}
          />
          <Stack.Screen
            name={routes.departmentDetails}
            component={DepartmentDetailsScreen}
          />
          <Stack.Screen
            name={routes.departmentCreate}
            component={DepartmentFormScreen}
          />
          <Stack.Screen
            name={routes.departmentEdit}
            component={DepartmentFormScreen}
          />
          <Stack.Screen
            name={routes.designationList}
            component={DesignationListScreen}
          />
          <Stack.Screen
            name={routes.designationDetails}
            component={DesignationDetailsScreen}
          />
          <Stack.Screen
            name={routes.designationCreate}
            component={DesignationFormScreen}
          />
          <Stack.Screen
            name={routes.designationEdit}
            component={DesignationFormScreen}
          />
          <Stack.Screen name={routes.teamList} component={TeamListScreen} />
          <Stack.Screen
            name={routes.teamDetails}
            component={TeamDetailsScreen}
          />
          <Stack.Screen name={routes.teamCreate} component={TeamFormScreen} />
          <Stack.Screen name={routes.teamEdit} component={TeamFormScreen} />
          <Stack.Screen name={routes.shiftList} component={ShiftListScreen} />
          <Stack.Screen
            name={routes.shiftDetails}
            component={ShiftDetailsScreen}
          />
          <Stack.Screen name={routes.shiftCreate} component={ShiftFormScreen} />
          <Stack.Screen name={routes.shiftEdit} component={ShiftFormScreen} />
          <Stack.Screen
            name={routes.workPolicyList}
            component={WorkPolicyListScreen}
          />
          <Stack.Screen
            name={routes.workPolicyDetails}
            component={WorkPolicyDetailsScreen}
          />
          <Stack.Screen
            name={routes.workPolicyCreate}
            component={WorkPolicyFormScreen}
          />
          <Stack.Screen
            name={routes.workPolicyEdit}
            component={WorkPolicyFormScreen}
          />
          <Stack.Screen name={routes.kioskList} component={KioskListScreen} />
          <Stack.Screen
            name={routes.kioskCreate}
            component={KioskCreateScreen}
          />
          <Stack.Screen
            name={routes.kioskDetails}
            component={KioskDetailsScreen}
          />
          <Stack.Screen
            name={routes.kioskActivationQr}
            component={KioskActivationQrScreen}
          />
          <Stack.Screen
            name={routes.projectList}
            component={ProjectListScreen}
          />
          <Stack.Screen
            name={routes.projectCreate}
            component={ProjectFormScreen}
          />
          <Stack.Screen
            name={routes.projectEdit}
            component={ProjectEditScreen}
          />
          <Stack.Screen
            name={routes.projectTaskBoard}
            component={ProjectTaskBoardScreen}
          />
          <Stack.Screen
            name={routes.projectTaskDetails}
            component={ProjectTaskDetailsScreen}
          />
          <Stack.Screen
            name={routes.projectReport}
            component={ProjectReportScreen}
          />
          <Stack.Screen
            name={routes.visitorList}
            component={VisitorListScreen}
          />
          <Stack.Screen
            name={routes.visitorRegister}
            component={VisitorRegisterScreen}
          />
          <Stack.Screen
            name={routes.visitorCheckIn}
            component={VisitorFaceScanScreen}
          />
          <Stack.Screen
            name={routes.visitorCheckOut}
            component={VisitorFaceScanScreen}
          />
          <Stack.Screen
            name={routes.visitorHistory}
            component={VisitorHistoryScreen}
          />
          <Stack.Screen
            name={routes.employeeDetails}
            component={EmployeeDetailsScreen}
          />
          <Stack.Screen
            name={routes.employeeTimeOffBalance}
            component={EmployeeTimeOffBalanceScreen}
          />
          <Stack.Screen
            name={routes.employeeEdit}
            component={EmployeeEditScreen}
          />
          <Stack.Screen
            name={routes.employeeAssignShift}
            component={EmployeeAssignShiftScreen}
          />
          <Stack.Screen
            name={routes.employeeCreate}
            component={EmployeeCreateScreen}
          />
          <Stack.Screen
            name={routes.employeeFaceRegistration}
            component={EmployeeFaceRegistrationScreen}
          />
          <Stack.Screen
            name={routes.faceEnrollmentCamera}
            component={FaceEnrollmentCameraScreen}
          />
          <Stack.Screen
            name={routes.faceIdentify}
            component={FaceIdentifyScreen}
          />
          <Stack.Screen name={routes.faceAdmin} component={FaceAdminScreen} />
          <Stack.Screen name={routes.leaveConfigurationList} component={LeaveConfigurationListScreen} />
          <Stack.Screen name={routes.leaveConfigurationCreate} component={LeaveConfigurationCreateScreen} />
          <Stack.Screen name={routes.leaveBalanceList} component={LeaveBalanceListScreen} />
          <Stack.Screen name={routes.leaveBalanceEmployee} component={LeaveBalanceEmployeeScreen} />
          <Stack.Screen name={routes.leaveBalanceUpdate} component={LeaveBalanceUpdateScreen} />
          <Stack.Screen name={routes.wfhConfigurationList} component={WfhConfigurationListScreen} />
          <Stack.Screen name={routes.wfhConfigurationCreate} component={WfhConfigurationCreateScreen} />
          {isTimePermissionEnabled() ? <Stack.Screen name={routes.permissionConfigurationList} component={PermissionConfigurationListScreen} /> : null}
          {isTimePermissionEnabled() ? <Stack.Screen name={routes.permissionConfigurationCreate} component={PermissionConfigurationCreateScreen} /> : null}
          {isTimePermissionEnabled() ? <Stack.Screen name={routes.permissionConfigurationPolicy} component={PermissionConfigurationPolicyScreen} /> : null}
          <Stack.Screen
            name={routes.officeCalendar}
            component={OfficeCalendarScreen}
          />
          <Stack.Screen
            name={routes.holidayCalendarList}
            component={HolidayCalendarListScreen}
          />
          <Stack.Screen
            name={routes.holidayCalendarCreate}
            component={HolidayCalendarFormScreen}
          />
          <Stack.Screen
            name={routes.holidayCalendarEdit}
            component={HolidayCalendarFormScreen}
          />
        </>
      ) : (
        <Stack.Screen name={routes.auth} component={AuthNavigator} />
      )}
    </Stack.Navigator>
  );
}
